/**
 * Preferences Context
 *
 * Provides operator UI preferences (pinned tabs, reporting favorites)
 * to all components via React Context.
 *
 * This is a thin wrapper around useOperatorPreferences hook to make
 * preferences available throughout the component tree without prop drilling.
 */
import React, { createContext, useContext, type ReactNode } from 'react';
import {
  useOperatorPreferences,
  type OperatorPreferencesData,
  type PersistedTab,
  type TabHistoryEntry,
  type RecentReportEntry
} from '@/hooks/useOperatorPreferences';

// ============================================================================
// Context Type
// ============================================================================

type PreferencesContextValue = {
  /** Current preferences */
  preferences: OperatorPreferencesData;
  /** Whether initial sync is in progress */
  isLoading: boolean;
  /** Whether a sync operation is in progress */
  isSyncing: boolean;
  /** Whether preferences are corrupted */
  isCorrupted: boolean;
  /** Error message if sync failed */
  error: string | null;

  // Tab preferences
  setPinnedTabs: (tabs: PersistedTab[]) => void;
  setRecentTabs: (tabs: PersistedTab[]) => void;
  setTabHistories: (histories: Record<string, TabHistoryEntry[]>) => void;
  setActiveTabPath: (path: string | null) => void;

  // Reporting preferences
  setReportingFavorites: (category: string, favorites: string[]) => void;
  toggleReportingFavorite: (category: string, reportType: string) => string[];
  setRecentReports: (category: string, recents: RecentReportEntry[]) => void;
  addRecentReport: (category: string, report: RecentReportEntry) => void;
  getReportingFavorites: (category: string) => string[];
  getRecentReports: (category: string) => RecentReportEntry[];

  // Sync operations
  syncNow: () => Promise<void>;
  resetPreferences: () => Promise<void>;
};

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

type PreferencesProviderProps = {
  children: ReactNode;
};

export function PreferencesProvider({ children }: PreferencesProviderProps) {
  const preferencesHook = useOperatorPreferences();

  return (
    <PreferencesContext.Provider value={preferencesHook}>
      {children}
    </PreferencesContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Use operator preferences from context
 * Must be used within a PreferencesProvider
 */
export function usePreferences(): PreferencesContextValue {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}

/**
 * Optional preferences hook - returns null if not in provider
 * Useful for components that may render outside the provider
 */
export function usePreferencesOptional(): PreferencesContextValue | null {
  return useContext(PreferencesContext);
}

// Re-export types for convenience
export type {
  OperatorPreferencesData,
  PersistedTab,
  TabHistoryEntry,
  RecentReportEntry
};
